// Resuelve, en runtime, el deck que se va a renderizar: parte del deck compilado y le
// aplica la config publicada (si existe). Warm cache de sessionStorage => primer paint
// instantáneo; igual revalida en background. Fallo/ausencia de config => deck compilado.

import { useEffect, useState } from "react";
import type { PublishedPayload } from "@shared/deckConfig";
import { getDeck } from "@/decks/registry";
import {
  buildAliases,
  buildOverrides,
  fetchDeckConfig,
  readWarm,
  resolveDeck,
} from "@/decks/runtimeConfig";
import type { DeckDef } from "@/decks/types";
import type { Locale, MessageOverrides, NamespaceAliases } from "@/i18n";

export interface DeckResolution {
  deck: DeckDef;
  overrides: MessageOverrides;
  aliases: NamespaceAliases;
  /**
   * Idiomas disponibles del deck resuelto. Vienen de `meta.languages` del payload publicado
   * (fuente de verdad para duplicados runtime) o, en fallback al compilado, de su DeckDef.
   */
  languages: Locale[];
}

export interface UseDeckConfig {
  resolution: DeckResolution | null;
  loading: boolean;
}

/**
 * Combina un payload publicado (o su ausencia) con el deck compilado. Devuelve null solo si
 * `deckId` no corresponde a ningún deck compilado y no hay component set publicado que sirva
 * de base (no debería ocurrir para los decks base).
 */
function resolvePayload(
  deckId: string,
  payload: PublishedPayload | null,
): DeckResolution | null {
  if (!payload) {
    const compiled = getDeck(deckId);
    return compiled
      ? { deck: compiled, overrides: {}, aliases: {}, languages: compiled.languages }
      : null;
  }
  const compiled = getDeck(payload.meta.componentSet) ?? getDeck(deckId);
  if (!compiled) return null;
  return {
    deck: resolveDeck(compiled, payload.config, deckId),
    overrides: buildOverrides(deckId, payload.config.messages),
    aliases: buildAliases(deckId, payload.meta.componentSet),
    // Los idiomas del deck real vienen de su meta (no del component set compilado).
    languages: payload.meta.languages,
  };
}

export function useDeckConfig(deckId: string, preview?: string): UseDeckConfig {
  // En modo preview no se usa warm cache: siempre se pide el borrador fresco.
  const [state, setState] = useState<UseDeckConfig>(() => {
    const warm = preview ? null : readWarm(deckId);
    if (warm) {
      return { resolution: resolvePayload(deckId, warm), loading: false };
    }
    return { resolution: null, loading: true };
  });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const warm = preview ? null : readWarm(deckId);
    setState(
      warm
        ? { resolution: resolvePayload(deckId, warm), loading: false }
        : { resolution: null, loading: true },
    );

    void fetchDeckConfig(deckId, controller.signal, preview).then((payload) => {
      if (!active) return;
      setState({ resolution: resolvePayload(deckId, payload), loading: false });
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [deckId, preview]);

  return state;
}
